// What the 3D viewer tells the site's anonymous analytics. static/js/analytics.js turns these document
// events into reports and does nothing when Do Not Track is on. A report names a control and a coarse
// value, never a camera or pointer position, a coordinate, or what the readout showed.

const MAX_REPORTS = 300;
const GESTURE_GAP_MS = 5000;
const SETTLE_MS = 700;

// A slider reports where it came to rest, in steps wide enough to compare between visitors.
export const depthBucket = (meters) => (meters > 0 ? `${Math.max(10, Math.round(meters / 10) * 10)} m` : "Surface");
export const exaggerationBucket = (factor) => `${Math.max(10, Math.round(factor / 10) * 10)}×`;
export const targetBucket = (celsius) => `${Math.round(celsius)} °C`;

export function createThreeDAnalytics({
  lake,
  now = () => performance.now(),
  target = document,
  wait = setTimeout,
  cancel = clearTimeout
}) {
  const sent = new Set();
  const lastGesture = new Map();
  const settling = new Map();
  const settled = new Map();
  let reports = 0;
  let moved = false;
  let under = false;
  let surfaced = false;

  const dispatch = (name, detail) => target.dispatchEvent(new CustomEvent(name, { detail }));

  // A page left open with something animating must not flood the endpoint.
  function report(name, detail) {
    if (reports >= MAX_REPORTS) return;
    reports += 1;
    dispatch(name, { lake: lake(), ...detail });
  }

  function interaction(action, value = "", source = "") {
    report("great-lakes-three-d-interaction", { action, value, ...(source ? { source } : {}) });
  }

  function once(action, value = "") {
    const key = `${action}:${value}`;
    if (sent.has(key)) return;
    sent.add(key);
    interaction(action, value);
  }

  function emitSettled(action, value) {
    settling.delete(action);
    if (settled.get(action) === value) return;
    settled.set(action, value);
    interaction(action, value);
  }

  function checkDive() {
    if (under && surfaced && moved) once("dived", "underwater");
  }

  return {
    // "lakebed" once the lakebed is drawn and "water" once the first lake's water data shows,
    // each timed from the start of the page load.
    ready(source) {
      const key = `ready:${source}`;
      if (sent.has(key)) return;
      sent.add(key);
      report("great-lakes-three-d-ready", { source, durationMs: Math.round(now()) });
    },
    interaction,
    once,
    // Orbit, pan and zoom come in bursts, so each is reported at most once every few seconds.
    camera(action) {
      moved = true;
      checkDive();
      const at = now();
      if (at - (lastGesture.get(action) ?? -Infinity) < GESTURE_GAP_MS) return;
      lastGesture.set(action, at);
      interaction(action);
    },
    // Told every frame whether the camera is under the surface. It only counts as a dive when the visitor
    // moved the camera and it had been above the water first, not when a link opened the page below it.
    underwater(isUnder) {
      under = isUnder;
      if (!isUnder) surfaced = true;
      checkDive();
    },
    // Sliders fire for every step; report the value they stop on.
    settle(action, value) {
      cancel(settling.get(action)?.timer);
      settling.set(action, { value, timer: wait(() => emitSettled(action, value), SETTLE_MS) });
    },
    // Reports any slider still settling, for when the page is being left.
    flush() {
      for (const [action, pending] of [...settling]) {
        cancel(pending.timer);
        emitSettled(action, pending.value);
      }
    },
    // `where` names the lake that failed when it is not the one in view, as with one lake of the five.
    issue(errorType, where = lake()) {
      const key = `issue:${errorType}:${where}`;
      if (sent.has(key)) return;
      sent.add(key);
      report("great-lakes-three-d-issue", { errorType, lake: where });
    },
    // The volume download counts as an API request, so it shows with the other endpoints' timings and failures.
    request(endpoint, startedAt, error = null) {
      const failure = !error ? "" : error.status ? "http" : error.name === "TypeError" ? "network" : "response_body";
      dispatch("great-lakes-api-request", {
        endpoint,
        durationMs: Math.max(0, Math.round(now() - startedAt)),
        status: error ? error.status || 0 : 200,
        success: !error,
        failure,
        requestId: "",
        online: navigator.onLine !== false
      });
    }
  };
}
